import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState
} from "react";
import { useApi } from "../hooks/useApi.js";

const defaultNode = () => ({ prompt: "", transitions: {} });

function FlowEditorPanel(
  { active = true, onClose, onToast, showCloseButton = true, onStatusChange, onFlowsChange, onDirtyChange },
  ref
) {
  const api = useApi();
  const [flows, setFlows] = useState({});
  const [savedFlows, setSavedFlows] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedFlows, setExpandedFlows] = useState({});
  const [expandedNodes, setExpandedNodes] = useState({});
  const [showJsonPreview, setShowJsonPreview] = useState(false);

  const loadFlows = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const { data } = await api.get("/flows");
      const loaded = data?.flows || {};
      setFlows(loaded);
      setSavedFlows(loaded);
      // Expandir todos los flujos por defecto
      const flowNames = Object.keys(loaded);
      setExpandedFlows(flowNames.reduce((acc, name) => ({ ...acc, [name]: true }), {}));
    } catch (err) {
      setError(err.response?.data?.message || "No se pudieron cargar los flujos");
      setFlows({});
      setSavedFlows({});
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (!active) return;
    loadFlows();
  }, [active, loadFlows]);

  useEffect(() => {
    onStatusChange?.({ loading, saving });
  }, [loading, saving, onStatusChange]);

  // Notify parent of flows changes (for graph view)
  useEffect(() => {
    onFlowsChange?.(flows);
  }, [flows, onFlowsChange]);

  // Notify parent of dirty state
  useEffect(() => {
    const dirty = JSON.stringify(flows) !== JSON.stringify(savedFlows);
    onDirtyChange?.(dirty);
  }, [flows, savedFlows, onDirtyChange]);

  const handleSave = useCallback(async () => {
    try {
      setSaving(true);
      setError("");
      await api.put("/flows", flows);
      setSavedFlows(flows);
      onToast?.("Flujos guardados");
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.message || "No se pudieron guardar los flujos");
    } finally {
      setSaving(false);
    }
  }, [api, flows, onClose, onToast]);

  useImperativeHandle(
    ref,
    () => ({
      reload: loadFlows,
      save: () => handleSave(),
      isBusy: loading || saving,
      dirty: JSON.stringify(flows) !== JSON.stringify(savedFlows),
    }),
    [handleSave, loadFlows, loading, saving, flows, savedFlows]
  );

  const flowEntries = useMemo(() => Object.entries(flows || {}), [flows]);
  const stats = useMemo(() => {
    const summary = { flows: flowEntries.length, nodes: 0, endNodes: 0, transitions: 0 };
    flowEntries.forEach(([, nodes]) => {
      const nodeList = Object.entries(nodes || {})
        .filter(([k]) => !k.startsWith("_"))
        .map(([, v]) => v);
      summary.nodes += nodeList.length;
      summary.endNodes += nodeList.filter((node) => node?.end).length;
      nodeList.forEach((node) => {
        summary.transitions += Object.keys(node?.transitions || {}).length;
      });
    });
    return summary;
  }, [flowEntries]);

  // Filtrar flujos por búsqueda
  const filteredFlowEntries = useMemo(() => {
    if (!searchTerm.trim()) return flowEntries;
    const term = searchTerm.toLowerCase();
    return flowEntries.filter(([flowName, nodes]) => {
      if (flowName.toLowerCase().includes(term)) return true;
      return Object.entries(nodes || {})
        .filter(([k]) => !k.startsWith("_"))
        .some(([nodeName, node]) =>
          nodeName.toLowerCase().includes(term) ||
          (node?.prompt || "").toLowerCase().includes(term)
        );
    });
  }, [flowEntries, searchTerm]);

  function toggleFlow(flowName) {
    setExpandedFlows(prev => ({ ...prev, [flowName]: !prev[flowName] }));
  }

  function toggleNode(flowName, nodeName) {
    const key = `${flowName}:${nodeName}`;
    setExpandedNodes(prev => ({ ...prev, [key]: !prev[key] }));
  }

  function expandAllFlows() {
    const all = flowEntries.reduce((acc, [name]) => ({ ...acc, [name]: true }), {});
    setExpandedFlows(all);
  }

  function collapseAllFlows() {
    setExpandedFlows({});
    setExpandedNodes({});
  }

  function getNodeFlags(nodeName, node) {
    const flags = [];
    if (nodeName === "start") flags.push("Inicio");
    if (node?.end) flags.push("Fin");
    if (!Object.keys(node?.transitions || {}).length && !node?.end) flags.push("Sin salida");
    return flags;
  }

  function mutateFlow(flowName, updater) {
    setFlows((previous) => {
      const clone = JSON.parse(JSON.stringify(previous || {}));
      if (!clone[flowName]) clone[flowName] = { start: defaultNode() };
      updater(clone[flowName], clone);
      return clone;
    });
  }

  function handleAddFlow() {
    const input = prompt("Nombre del nuevo flujo (sin espacios)");
    if (!input) return;
    const flowName = input.trim().replace(/\s+/g, "_");
    if (!flowName) return;
    if (flows?.[flowName]) {
      setError(`Ya existe un flujo llamado "${flowName}"`);
      return;
    }
    setError("");
    setFlows((previous) => {
      const existing = previous || {};
      return {
        [flowName]: { start: defaultNode() },
        ...existing
      };
    });
    setExpandedFlows(prev => ({ ...prev, [flowName]: true }));
  }

  function handleDeleteFlow(flowName) {
    if (!flows?.[flowName]) return;
    const confirmed = window.confirm(`¿Eliminar el flujo "${flowName}" y todos sus nodos?`);
    if (!confirmed) return;
    setFlows((previous) => {
      const clone = { ...(previous || {}) };
      delete clone[flowName];
      return clone;
    });
  }

  function handleDuplicateFlow(flowName) {
    const newName = prompt("Nombre del flujo duplicado", `${flowName}_copy`);
    if (!newName) return;
    const trimmed = newName.trim().replace(/\s+/g, "_");
    if (flows?.[trimmed]) {
      setError(`Ya existe un flujo llamado "${trimmed}"`);
      return;
    }
    setFlows((previous) => ({
      ...previous,
      [trimmed]: JSON.parse(JSON.stringify(previous[flowName]))
    }));
    setExpandedFlows(prev => ({ ...prev, [trimmed]: true }));
  }

  function handlePromptChange(flowName, nodeName, value) {
    mutateFlow(flowName, (flow) => {
      flow[nodeName] = flow[nodeName] || defaultNode();
      flow[nodeName].prompt = value;
    });
  }

  function handleTransitionKeyChange(flowName, nodeName, oldKey, newKey) {
    mutateFlow(flowName, (flow) => {
      const transitions = { ...(flow[nodeName]?.transitions || {}) };
      const value = transitions[oldKey];
      delete transitions[oldKey];
      transitions[newKey] = value;
      flow[nodeName].transitions = transitions;
    });
  }

  function handleTransitionValueChange(flowName, nodeName, key, newValue) {
    mutateFlow(flowName, (flow) => {
      flow[nodeName].transitions = flow[nodeName].transitions || {};
      flow[nodeName].transitions[key] = newValue;
    });
  }

  function handleAddTransition(flowName, nodeName) {
    mutateFlow(flowName, (flow) => {
      flow[nodeName].transitions = flow[nodeName].transitions || {};
      let index = 1;
      let key = `opcion_${index}`;
      while (flow[nodeName].transitions[key]) {
        index += 1;
        key = `opcion_${index}`;
      }
      flow[nodeName].transitions[key] = "";
    });
  }

  function handleRemoveTransition(flowName, nodeName, transitionKey) {
    mutateFlow(flowName, (flow) => {
      if (flow[nodeName]?.transitions) {
        delete flow[nodeName].transitions[transitionKey];
      }
    });
  }

  function handleToggleEnd(flowName, nodeName) {
    mutateFlow(flowName, (flow) => {
      flow[nodeName].end = !flow[nodeName].end;
    });
  }

  function handleAddNode(flowName) {
    const nodeName = prompt("Nombre del nuevo nodo (sin espacios)");
    if (!nodeName) return;
    const trimmed = nodeName.trim().replace(/\s+/g, "_");
    mutateFlow(flowName, (flow) => {
      if (flow[trimmed]) return;
      flow[trimmed] = defaultNode();
    });
    const key = `${flowName}:${trimmed}`;
    setExpandedNodes(prev => ({ ...prev, [key]: true }));
  }

  function handleRenameNode(flowName, oldName) {
    if (oldName === "start") {
      setError("No se puede renombrar el nodo 'start'");
      return;
    }
    const newName = prompt("Nuevo nombre del nodo", oldName);
    if (!newName || newName === oldName) return;
    const trimmed = newName.trim().replace(/\s+/g, "_");
    mutateFlow(flowName, (flow) => {
      if (flow[trimmed]) return;
      flow[trimmed] = flow[oldName];
      delete flow[oldName];
      // Actualizar referencias en transiciones
      Object.values(flow).forEach(node => {
        if (node?.transitions) {
          Object.keys(node.transitions).forEach(key => {
            if (node.transitions[key] === oldName) {
              node.transitions[key] = trimmed;
            }
          });
        }
      });
    });
  }

  function handleDeleteNode(flowName, nodeName) {
    if (nodeName === "start") {
      setError("No se puede eliminar el nodo 'start'");
      return;
    }
    const confirmed = window.confirm(`¿Eliminar el nodo "${nodeName}"?`);
    if (!confirmed) return;
    mutateFlow(flowName, (flow) => {
      delete flow[nodeName];
    });
  }

  // Obtener lista de nodos disponibles para transiciones
  function getAvailableNodes(flowName) {
    const nodes = flows?.[flowName] || {};
    return Object.keys(nodes).filter((k) => !k.startsWith("_"));
  }

  return (
    <div className="flow-editor-panel">
      <header className="flow-editor-header">
        <div>
          <h2>Editor de Flujos</h2>
          <p className="flow-editor-subtitle">
            Diseñá las rutas conversacionales del bot. Cada flujo tiene nodos conectados por transiciones.
          </p>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <button className="button-secondary" onClick={handleAddFlow} disabled={saving || loading}>
            + Nuevo flujo
          </button>
          {showCloseButton && (
            <button className="button-secondary" onClick={onClose} disabled={saving}>
              Cerrar
            </button>
          )}
          <button
            type="button"
            className="button-primary"
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </header>

      {error && <div className="toast" style={{ position: "relative", marginBottom: "12px" }}>{error}</div>}

      {loading ? (
        <div className="empty-state">
          <span>Cargando flujos...</span>
        </div>
      ) : (
        <div className="flow-editor-content-v2">
          {/* Barra de herramientas */}
          <div className="flow-toolbar">
            <div className="flow-search">
              <input
                type="text"
                placeholder="🔍 Buscar flujos o nodos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button className="flow-search-clear" onClick={() => setSearchTerm("")}>✕</button>
              )}
            </div>
            <div className="flow-toolbar-actions">
              <button className="button-icon" onClick={expandAllFlows} title="Expandir todo">▼</button>
              <button className="button-icon" onClick={collapseAllFlows} title="Colapsar todo">▲</button>
              <label className="flow-toggle-json">
                <input
                  type="checkbox"
                  checked={showJsonPreview}
                  onChange={(e) => setShowJsonPreview(e.target.checked)}
                />
                JSON
              </label>
            </div>
            <div className="flow-stats-inline">
              <span><strong>{stats.flows}</strong> flujos</span>
              <span><strong>{stats.nodes}</strong> nodos</span>
              <span><strong>{stats.transitions}</strong> transiciones</span>
            </div>
          </div>

          {/* Lista de flujos */}
          <div className="flow-list">
            {filteredFlowEntries.length === 0 && searchTerm && (
              <div className="flow-empty-search">
                No se encontraron flujos o nodos que coincidan con "{searchTerm}"
              </div>
            )}

            {filteredFlowEntries.map(([flowName, nodes]) => {
              const isExpanded = expandedFlows[flowName];
              const nodeEntries = Object.entries(nodes || {}).filter(([k]) => !k.startsWith("_"));
              const endCount = nodeEntries.filter(([, n]) => n?.end).length;

              return (
                <div key={flowName} className={`flow-accordion ${isExpanded ? "expanded" : ""}`}>
                  <div className="flow-accordion-header" onClick={() => toggleFlow(flowName)}>
                    <div className="flow-accordion-title">
                      <span className="flow-accordion-icon">📋</span>
                      <strong>{flowName}</strong>
                      <span className="flow-accordion-meta">
                        {nodeEntries.length} nodos • {endCount} finales
                      </span>
                    </div>
                    <div className="flow-accordion-actions" onClick={(e) => e.stopPropagation()}>
                      <button className="button-icon" onClick={() => handleDuplicateFlow(flowName)} title="Duplicar flujo">📋</button>
                      <button className="button-icon danger" onClick={() => handleDeleteFlow(flowName)} title="Eliminar flujo">🗑️</button>
                      <span className="flow-accordion-toggle">{isExpanded ? "▼" : "▶"}</span>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="flow-accordion-content">
                      <div className="flow-nodes-toolbar">
                        <button className="button-secondary button-sm" onClick={() => handleAddNode(flowName)}>
                          + Añadir nodo
                        </button>
                        <span className="flow-nodes-hint">
                          El nodo "start" es el punto de entrada del flujo
                        </span>
                      </div>

                      <div className="flow-nodes-list">
                        {nodeEntries.map(([nodeName, node]) => {
                          const nodeKey = `${flowName}:${nodeName}`;
                          const isNodeExpanded = expandedNodes[nodeKey];
                          const flags = getNodeFlags(nodeName, node);
                          const transitionCount = Object.keys(node?.transitions || {}).length;
                          const promptPreview = (node?.prompt || "").substring(0, 80);
                          const availableNodes = getAvailableNodes(flowName);

                          return (
                            <div 
                              key={nodeName} 
                              className={`flow-node-accordion ${isNodeExpanded ? "expanded" : ""} ${node?.end ? "is-end" : ""} ${nodeName === "start" ? "is-start" : ""}`}
                            >
                              <div className="flow-node-header" onClick={() => toggleNode(flowName, nodeName)}>
                                <div className="flow-node-title-row">
                                  <div className="flow-node-name">
                                    <strong>{nodeName}</strong>
                                    <div className="flow-chip-row">
                                      {flags.map((flag) => (
                                        <span
                                          key={flag}
                                          className={`flow-chip ${flag === "Inicio" ? "start" : flag === "Fin" ? "end" : "warning"}`}
                                        >
                                          {flag}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                  {!isNodeExpanded && promptPreview && (
                                    <span className="flow-node-preview">{promptPreview}...</span>
                                  )}
                                </div>
                                <div className="flow-node-actions" onClick={(e) => e.stopPropagation()}>
                                  <span className="flow-node-transition-count" title="Transiciones">
                                    {transitionCount} →
                                  </span>
                                  {nodeName !== "start" && (
                                    <>
                                      <button className="button-icon" onClick={() => handleRenameNode(flowName, nodeName)} title="Renombrar">✏️</button>
                                      <button className="button-icon danger" onClick={() => handleDeleteNode(flowName, nodeName)} title="Eliminar">🗑️</button>
                                    </>
                                  )}
                                  <span className="flow-node-toggle">{isNodeExpanded ? "▼" : "▶"}</span>
                                </div>
                              </div>

                              {isNodeExpanded && (
                                <div className="flow-node-content">
                                  <div className="flow-node-settings">
                                    <label className="flow-field">
                                      <span>Mensaje para el cliente</span>
                                      <textarea
                                        value={node?.prompt || ""}
                                        onChange={(e) => handlePromptChange(flowName, nodeName, e.target.value)}
                                        placeholder="Ej: Contanos por qué deseás darte de baja..."
                                        rows={3}
                                      />
                                    </label>
                                    <div className="flow-node-options">
                                      <label className="flow-checkbox">
                                        <input
                                          type="checkbox"
                                          checked={node?.end || false}
                                          onChange={() => handleToggleEnd(flowName, nodeName)}
                                        />
                                        Nodo final (cierra el flujo)
                                      </label>
                                    </div>
                                  </div>

                                  <div className="flow-transitions-section">
                                    <div className="flow-transitions-header">
                                      <span>Transiciones</span>
                                      <button className="button-secondary button-sm" onClick={() => handleAddTransition(flowName, nodeName)}>
                                        + Añadir
                                      </button>
                                    </div>
                                    <p className="flow-hint">Define palabras clave y el nodo destino. Usa * como comodín.</p>

                                    {Object.entries(node?.transitions || {}).length > 0 ? (
                                      <div className="flow-transitions-list">
                                        {Object.entries(node.transitions).map(([pattern, target]) => (
                                          <div key={pattern} className="flow-transition-item">
                                            <div className="flow-transition-pattern">
                                              <label>Cuando dice</label>
                                              <input
                                                value={pattern}
                                                onChange={(e) => handleTransitionKeyChange(flowName, nodeName, pattern, e.target.value)}
                                                placeholder="si | no | *"
                                              />
                                            </div>
                                            <span className="flow-transition-arrow">→</span>
                                            <div className="flow-transition-target">
                                              <label>Ir a</label>
                                              <select
                                                value={target}
                                                onChange={(e) => handleTransitionValueChange(flowName, nodeName, pattern, e.target.value)}
                                              >
                                                <option value="">-- Seleccionar nodo --</option>
                                                {availableNodes.map(n => (
                                                  <option key={n} value={n}>{n}</option>
                                                ))}
                                              </select>
                                            </div>
                                            <button 
                                              className="button-icon danger" 
                                              onClick={() => handleRemoveTransition(flowName, nodeName, pattern)}
                                              title="Eliminar transición"
                                            >
                                              ✕
                                            </button>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <div className="flow-transitions-empty">
                                        {node?.end 
                                          ? "Nodo final - no necesita transiciones" 
                                          : "Sin transiciones. El flujo no podrá continuar desde aquí."}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {flowEntries.length === 0 && (
              <div className="flow-empty-state">
                <span className="flow-empty-icon">🔀</span>
                <h4>No hay flujos definidos</h4>
                <p>Los flujos guían conversaciones complejas paso a paso.</p>
                <button className="button-primary" onClick={handleAddFlow}>
                  Crear primer flujo
                </button>
              </div>
            )}
          </div>

          {/* Preview JSON */}
          {showJsonPreview && (
            <div className="flow-json-panel">
              <div className="flow-json-header">
                <h4>Vista JSON</h4>
                <button className="button-icon" onClick={() => setShowJsonPreview(false)}>✕</button>
              </div>
              <pre>{JSON.stringify(flows, null, 2)}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default forwardRef(FlowEditorPanel);
